/**
 * Authentication Service for Keycloak Integration
 */

import axios from 'axios';
import { encrypt } from '../utils/encryptionUtils';

const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || 'http://103.118.16.53:9090/';
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'carex';
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'carex-api';
const KEYCLOAK_CLIENT_SECRETE = "CWdrJwMrdwkIuNr4KeX3sqYKH3HG2r3p";


interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token?: string;
  'not-before-policy': number;
  session_state: string;
  scope: string;
}

interface DecodedToken {
  sub: string;
  exp?: number;
  iat?: number;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles: string[];
    };
  };

  facility?: { id: string; name: string };
  tenantId?: string;
}

export interface ResetOwnPasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

class AuthService {
  private refreshPromise: Promise<string | null> | null = null;
  // private tokenEndpoint = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
  // private logoutEndpoint = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`;

  /**
   * Login with username and password
   */
  async login(username: string, password: string): Promise<{ token: string; user: any }> {
    const loginSecurityKey = 'login_security_state';
    let securityStateStr = localStorage.getItem(loginSecurityKey) || '{}';
    let securityState: Record<string, { failedAttempts: number; locked: boolean }> = {};
    try {
      securityState = JSON.parse(securityStateStr);
    } catch (e) {
      securityState = {};
    }
    
    const roleCache = JSON.parse(localStorage.getItem('role_cache') || '{}');
    const cachedRole = roleCache[username];
    const usernameLower = username.toLowerCase();
    const isKnownAdminUsername = usernameLower.includes('admin');
    const isLockableRole = cachedRole === 'CAREGIVER' || cachedRole === 'LEAD_CAREGIVER' || cachedRole === 'FACILITY_ADMIN' || cachedRole === 'NURSE';
    const shouldApplyLock = isLockableRole || (!cachedRole && !isKnownAdminUsername);

    let userState = securityState[username] || { failedAttempts: 0, locked: false };

    if (shouldApplyLock && userState.locked) {
      throw new Error('Your account has been locked. Please contact your Administrator.');
    }

    try {
      const loginUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/users/login`;
      const payload = {
        username: encrypt(username),
        password: encrypt(password)
      };

      const response = await axios.post(
        loginUrl,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Map response to expected format
      const data = response.data;
      // Handle various potential token fields
      const access_token = data.access_token || data.token;
      const refresh_token = data.refresh_token || data.refreshToken;

      if (!access_token) {
        throw new Error('No access token received from server');
      }

      // Decode the JWT token to get user info
      const decodedToken = this.decodeToken(access_token);

      // Store tokens
      localStorage.setItem('authToken', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }

      // Determine user role from token
      const role = this.extractRole(decodedToken);
      const user = {
        id: decodedToken.sub,
        username: decodedToken.preferred_username || username,
        email: decodedToken.email,
        name: decodedToken.name || `${decodedToken.given_name} ${decodedToken.family_name}`,
        role,
        token: access_token,
        facility: decodedToken.facility || null,
        tenantId: decodedToken.tenantId || null
      };

      // Reset failed attempts on success and cache role
      if (userState.failedAttempts > 0) {
        userState.failedAttempts = 0;
        securityState[username] = userState;
        localStorage.setItem(loginSecurityKey, JSON.stringify(securityState));
      }
      const roleCache = JSON.parse(localStorage.getItem('role_cache') || '{}');
      roleCache[username] = role;
      localStorage.setItem('role_cache', JSON.stringify(roleCache));

      return { token: access_token, user };
    } catch (error: any) {
      console.error('Login error:', error);
      
      // If we threw the locked error earlier, rethrow it
      if (error.message === 'Your account has been locked. Please contact your Administrator.') {
        throw error;
      }
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      }
      
      if (error.response?.status === 401) {
        const roleCache = JSON.parse(localStorage.getItem('role_cache') || '{}');
        const cachedRole = roleCache[username];
        const usernameLower = username.toLowerCase();
        const isKnownAdminUsername = usernameLower.includes('admin');
        const isLockableRole = cachedRole === 'CAREGIVER' || cachedRole === 'LEAD_CAREGIVER' || cachedRole === 'FACILITY_ADMIN' || cachedRole === 'NURSE';
        const shouldApplyLock = isLockableRole || (!cachedRole && !isKnownAdminUsername);

        const errorMessage = error.response.data?.message || 'Invalid username or password';

        if (shouldApplyLock) {
          userState.failedAttempts += 1;
          if (userState.failedAttempts > 3) {
            userState.locked = true;
            securityState[username] = userState;
            localStorage.setItem(loginSecurityKey, JSON.stringify(securityState));
            throw new Error('Your account has been locked. Please contact your Administrator.');
          } else {
            securityState[username] = userState;
            localStorage.setItem(loginSecurityKey, JSON.stringify(securityState));
            throw new Error(errorMessage);
          }
        } else {
          throw new Error(errorMessage);
        }
      }
      
      throw new Error('Authentication failed. Please try again.');
    }
  }


  /**
   * Refresh the access token using refresh token
   */
  async refreshToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return null;

      try {
        const keycloakTokenUrl = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
        const response = await axios.post(
          keycloakTokenUrl,
          new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: KEYCLOAK_CLIENT_ID,
            client_secret: KEYCLOAK_CLIENT_SECRETE,
            refresh_token: refreshToken,
          }).toString(),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );

        const data = response.data;
        const access_token = data.access_token || data.token;
        const new_refresh_token = data.refresh_token || data.refreshToken;

        if (!access_token) {
          throw new Error('No access token received from refresh');
        }

        localStorage.setItem('authToken', access_token);
        if (new_refresh_token) {
          localStorage.setItem('refreshToken', new_refresh_token);
        }
        return access_token;
      } catch (error: any) {
        console.error('Refresh token error:', error);
        // Clear tokens on failure only if it's an explicit auth rejection from the server
        if (error.response && [400, 401, 403].includes(error.response.status)) {
          // Don't call this.logout() here — let the caller decide whether to log out.
          // Calling logout() here creates a race condition that clears tokens while
          // other parts of the app may still be trying to refresh.
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
        }
        return null;
      }
    })();

    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  /**
   * Logout from the application
   */
  async logout(): Promise<void> {
    try {
      const logoutUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/users/logout`;
      const token = localStorage.getItem('authToken');

      if (token) {
        await axios.post(
          logoutUrl,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still proceed with clearing local storage even if API fails
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  /**
   * Decode JWT token
   */
  private decodeToken(token: string): DecodedToken {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      throw new Error('Invalid token');
    }
  }

  /**
   * Extract user role from token
   */
  private extractRole(decodedToken: DecodedToken): string {
    // Check carex-api resource roles first (client-specific roles)
    const carexApiRoles = decodedToken.resource_access?.['carex-api']?.roles || [];

    // Also check realm roles as fallback
    const realmRoles = decodedToken.realm_access?.roles || [];

    // Combine both role arrays and normalize (uppercase, remove ROLE_ prefix)
    const allRoles = [...carexApiRoles, ...realmRoles].map(r =>
      r.toString().toUpperCase().replace(/^ROLE_/, "")
    );

    if (allRoles.includes('SUPER_ADMIN')) {
      return 'SUPER_ADMIN';
    }
    if (allRoles.includes('CLIENT_ADMIN')) {
      return 'CLIENT_ADMIN';
    }
    if (allRoles.includes('FACILITY_ADMIN')) {
      return 'FACILITY_ADMIN';
    }
    if (allRoles.includes('ADMIN')) {
      return 'ADMIN';
    }
    if (allRoles.includes('DOCTOR')) {
      return 'DOCTOR';
    }
    if (allRoles.includes('LEAD_CAREGIVER')) {
      return 'LEAD_CAREGIVER';
    }
    if (allRoles.includes('CAREGIVER')) {
      return 'CAREGIVER';
    }
    if (allRoles.includes('NURSE')) {
      return 'NURSE';
    }
    if (allRoles.includes('SOCIAL_WORKER')) {
      return 'SOCIAL_WORKER';
    }

    // Default role
    return 'NURSE';
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      const currentTime = Date.now() / 1000;
      // Add a 60-second buffer to prevent tokens expiring during transit
      return decoded['exp'] ? decoded['exp'] < (currentTime + 60) : true;
    } catch {
      return true;
    }
  }

  /**
   * Send forgot password email
   */
  async forgotPassword(email: string): Promise<void> {
    const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
    const url = `${authBaseUrl}/forgot-password-mail`;
    try {
      await axios.post(
        url,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to send reset email. Please try again.'
      );
    }
  }

  /**
   * Reset own password (requires auth token)
   */
  async resetOwnPassword(data: ResetOwnPasswordRequest): Promise<void> {
    const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
    const url = `${authBaseUrl}/reset-own`;
    
    const encryptedData = {
      currentPassword: encrypt(data.currentPassword),
      newPassword: encrypt(data.newPassword),
      confirmPassword: encrypt(data.confirmPassword)
    };
    
    try {
      const encryptedData = {
        currentPassword: encrypt(data.currentPassword),
        newPassword: encrypt(data.newPassword),
        confirmPassword: encrypt(data.confirmPassword)
      };
      await axios.put(
        url,
        encryptedData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
        }
      );
    } catch (error: any) {
      console.error('Reset own password error:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to reset password. Please check your current password and try again.'
      );
    }
  }

  /**
   * Reset password using a token (from email link)
   */
  async resetPasswordWithToken(token: string, username: string, newPassword: string, confirmPassword: string): Promise<void> {
    const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
    const url = `${authBaseUrl}/reset-password-bymail`;
    try {
      await axios.post(
        url,
        {
          token,
          username: encrypt(username),
          newPassword: encrypt(newPassword),
          confirmPassword: encrypt(confirmPassword)
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error: any) {
      console.error('Reset password with token error:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to reset password. The token may be invalid or expired.'
      );
    }
  }
}

export default new AuthService();
